import React, { useState, useEffect, useCallback } from "react";
import { authFetch, apiUrl } from "../../../utils/api";

/**
 * Team tab — the menu face of team workspaces (Melissa call item #12).
 * Everything here is also doable by just telling Ollie ("share this with my
 * team") — this tab makes it visible and manageable without a conversation.
 */
const TeamTab = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState({}); // teamId -> {members, shared}
  const [inviteEmail, setInviteEmail] = useState({}); // teamId -> email draft
  const [folderDraft, setFolderDraft] = useState({}); // teamId -> selected folder
  const [myFolders, setMyFolders] = useState([]); // actual folders from the workspace
  const [seats, setSeats] = useState(null); // {included, purchased, used, billing}

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(apiUrl("/api/teams"));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load teams");
      const teamList = data.teams || [];
      setTeams(teamList);
      setLoading(false); // paint the team list NOW — details stream in below

      // Lazy hydration: seats + all per-team details in ONE parallel wave
      // (was: serial seats fetch, then a serial loop per team — the reason
      // this menu took ages to populate).
      const seatsP = (async () => {
        try {
          const sRes = await authFetch(apiUrl("/api/teams/seats"));
          const sData = await sRes.json();
          if (sRes.ok) setSeats(sData.seats || null);
        } catch (_) { /* seat meter is optional */ }
      })();
      await Promise.all([
        seatsP,
        ...teamList.map(async (t) => {
          try {
            const [mRes, sRes] = await Promise.all([
              authFetch(apiUrl(`/api/teams/${t.id}/members`)),
              authFetch(apiUrl(`/api/teams/${t.id}/shared`)),
            ]);
            const m = await mRes.json();
            const sh = await sRes.json();
            setDetails((prev) => ({ ...prev, [t.id]: { members: m.members || [], shared: sh.shared || { conversations: [], skills: [], tools: [], folders: [] } } }));
          } catch (_) {
            setDetails((prev) => ({ ...prev, [t.id]: { members: [], shared: { conversations: [], skills: [], tools: [], folders: [] } } }));
          }
        }),
      ]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Folder picker options — the user's REAL folders, not a type-a-path box.
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(apiUrl("/api/c3/files"));
        const data = await res.json();
        if (res.ok) {
          const folders = (data.files || [])
            .filter((f) => f.type === "folder")
            .map((f) => f.path)
            .sort();
          setMyFolders(folders);
        }
      } catch (_) { /* picker degrades to empty */ }
    })();
  }, []);

  const createTeam = async () => {
    if (!newTeamName.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(apiUrl("/api/teams"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newTeamName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Couldn't create team");
      setNewTeamName("");
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const addMember = async (teamId) => {
    const email = (inviteEmail[teamId] || "").trim();
    if (!email || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(apiUrl(`/api/teams/${teamId}/members`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Couldn't add member");
      setInviteEmail((p) => ({ ...p, [teamId]: "" }));
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const removeMember = async (teamId, userId, email) => {
    if (!window.confirm(`Remove ${email || userId} from the team?`)) return;
    setBusy(true);
    try {
      await authFetch(apiUrl(`/api/teams/${teamId}/members/${userId}`), { method: "DELETE" });
      await load();
    } finally { setBusy(false); }
  };

  const addSeat = async () => {
    if (!window.confirm("Add 1 team seat for $97/month? It's billed to your existing Optimus subscription, prorated by Stripe.")) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(apiUrl("/api/teams/seats/add"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity: 1 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Couldn't add the seat");
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const shareFolder = async (teamId) => {
    const folder = (folderDraft[teamId] || "").trim().replace(/^\/+|\/+$/g, "");
    if (!folder || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(apiUrl(`/api/teams/${teamId}/share`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resource_type: "folder", resource_id: folder }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Couldn't share folder");
      setFolderDraft((p) => ({ ...p, [teamId]: "" }));
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const unshare = async (resourceType, resourceId, label) => {
    if (!window.confirm(`Make "${label}" private again? Team members will lose access.`)) return;
    setBusy(true);
    try {
      await authFetch(apiUrl(`/api/teams/unshare`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resource_type: resourceType, resource_id: resourceId }),
      });
      await load();
    } finally { setBusy(false); }
  };

  const card = { background: "var(--bg-tertiary)", borderRadius: 10, padding: 16, marginBottom: 16 };
  const btn = { padding: "8px 14px", borderRadius: 8, border: "none", background: "#00d4ff", color: "#0b0e14", fontWeight: 600, cursor: "pointer" };
  const btnGhost = { ...btn, background: "transparent", color: "var(--text-secondary)", border: "1px solid var(--border-primary)", fontWeight: 500 };
  const input = { padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border-primary)", background: "var(--bg-secondary)", color: "var(--text-primary)" };

  if (loading) return <div className="settings-section"><p>Loading teams…</p></div>;

  return (
    <div className="settings-section">
      <h3>👥 Team Workspaces</h3>
      <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 20 }}>
        Share chats, skills, and custom tools with your team — each person works with
        their <strong>own</strong> Claude and credentials. Nothing is shared until you share it.
        Members must be on your email domain. Your plan includes <strong>5 team seats</strong>;
        additional seats are $97/mo. You can also just tell Ollie:
        <em> "share this with my team"</em>.
      </p>

      {error && (
        <div style={{ ...card, borderLeft: "4px solid var(--accent-warning, #f59e0b)" }}>
          <p style={{ margin: 0, fontSize: 14 }}>{error}</p>
        </div>
      )}

      {/* Seats */}
      {seats && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div>
              <strong style={{ fontSize: 14 }}>
                Team seats: {seats.used} of {seats.included + seats.purchased} in use
              </strong>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>
                {seats.included} included with your plan{seats.purchased > 0 ? ` + ${seats.purchased} purchased` : ""} · additional seats $97/mo
              </div>
            </div>
            {seats.billing === "stripe" && (
              <button style={btnGhost} onClick={addSeat} disabled={busy}>
                + Add Seat ($97/mo)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Create */}
      <div style={card}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={{ ...input, flex: 1 }}
            placeholder="New team name (e.g. Make More Marbles)"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createTeam()}
            disabled={busy}
          />
          <button style={btn} onClick={createTeam} disabled={busy || !newTeamName.trim()}>
            Create Team
          </button>
        </div>
      </div>

      {teams.length === 0 && (
        <p style={{ color: "var(--text-secondary)", fontSize: 14 }}>
          No teams yet — create one above, then add teammates by email.
        </p>
      )}

      {teams.map((t) => {
        const d = details[t.id] || { members: [], shared: { conversations: [], skills: [], tools: [], folders: [] } };
        const isOwner = t.role === "owner";
        const sharedCount = d.shared.conversations.length + d.shared.skills.length + d.shared.tools.length + (d.shared.folders || []).length;
        return (
          <div key={t.id} style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <h4 style={{ margin: 0 }}>{t.name}</h4>
              <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                {t.member_count} member{t.member_count === 1 ? "" : "s"} · {sharedCount} shared item{sharedCount === 1 ? "" : "s"} · you are {t.role}
              </span>
            </div>

            {/* Members */}
            <div style={{ marginTop: 12 }}>
              {d.members.map((m) => (
                <div key={m.user_id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border-primary)", fontSize: 14 }}>
                  <span>{m.email || m.user_id} {m.role === "owner" && <em style={{ color: "var(--text-secondary)" }}>(owner)</em>}</span>
                  {isOwner && m.role !== "owner" && (
                    <button style={{ ...btnGhost, padding: "2px 10px", fontSize: 12 }} onClick={() => removeMember(t.id, m.user_id, m.email)} disabled={busy}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
              {isOwner && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <input
                    style={{ ...input, flex: 1 }}
                    placeholder="teammate@your-domain.com"
                    value={inviteEmail[t.id] || ""}
                    onChange={(e) => setInviteEmail((p) => ({ ...p, [t.id]: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && addMember(t.id)}
                    disabled={busy}
                  />
                  <button style={btn} onClick={() => addMember(t.id)} disabled={busy}>Add</button>
                </div>
              )}
            </div>

            {/* Share a folder */}
            {isOwner && (
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <select
                  style={{ ...input, flex: 1 }}
                  value={folderDraft[t.id] || ""}
                  onChange={(e) => setFolderDraft((p) => ({ ...p, [t.id]: e.target.value }))}
                  disabled={busy}
                >
                  <option value="">
                    {myFolders.length ? "Choose a folder to share (read/write for team + agents)…" : "No folders yet — create one in Files first"}
                  </option>
                  {myFolders
                    .filter((f) => !(d.shared.folders || []).some((sf) => sf.folder_path === f))
                    .map((f) => (
                      <option key={f} value={f}>📁 {f}</option>
                    ))}
                </select>
                <button style={btnGhost} onClick={() => shareFolder(t.id)} disabled={busy}>Share Folder</button>
              </div>
            )}

            {/* Shared items */}
            {sharedCount > 0 && (
              <div style={{ marginTop: 14 }}>
                <strong style={{ fontSize: 13 }}>Shared with this team</strong>
                {d.shared.conversations.map((c) => (
                  <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}>
                    <span>💬 {c.title || "Untitled chat"}</span>
                    <button style={{ ...btnGhost, padding: "1px 8px", fontSize: 12 }} onClick={() => unshare("conversation", c.id, c.title || "chat")} disabled={busy}>Unshare</button>
                  </div>
                ))}
                {d.shared.skills.map((sk) => (
                  <div key={sk.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}>
                    <span>🧰 {sk.name || sk.slug}</span>
                    <button style={{ ...btnGhost, padding: "1px 8px", fontSize: 12 }} onClick={() => unshare("skill", sk.id, sk.name || sk.slug)} disabled={busy}>Unshare</button>
                  </div>
                ))}
                {(d.shared.folders || []).map((f) => (
                  <div key={f.folder_path} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}>
                    <span>📁 {f.folder_path} <em style={{ color: "var(--text-secondary)" }}>(read/write, team + agents)</em></span>
                    <button style={{ ...btnGhost, padding: "1px 8px", fontSize: 12 }} onClick={() => unshare("folder", f.folder_path, f.folder_path)} disabled={busy}>Unshare</button>
                  </div>
                ))}
                {d.shared.tools.map((tool) => (
                  <div key={tool.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}>
                    <span>🔧 {tool.display_name || tool.name}</span>
                    <button style={{ ...btnGhost, padding: "1px 8px", fontSize: 12 }} onClick={() => unshare("tool", tool.id, tool.display_name || tool.name)} disabled={busy}>Unshare</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TeamTab;
